import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { pipe, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { BroadcastAction, BroadcastService } from '../broadcast.service';
import { PlayerService } from '../services/player.service';
import { CharCodes } from '../domain/char-codes';
import {
  DataSet,
  Game,
  GameOfficial,
  OfficialRoleInGame
} from '../domain/game';
import {
  AttackScenario,
  DefensiveReaction,
  defensiveReactions,
  EventType,
  GameIncidentReason,
  GameIncidentReasonType,
  GameIncidentSituation,
  GameIncidentType,
  GamePeriod,
  HighlightPlayback,
  HighlightRating,
  HighlightType,
  InterruptionType,
  OfficialsCallAction,
  OfficialsCallSituation,
  PassOutcome,
  PassType,
  PenaltyType,
  ShotOutcome,
  ShotScenario,
  ShotType,
  StrengthState,
  TeamFaceOffOutcome,
  VideoTagType
} from '../domain/game-event';
import { Team } from '../domain/team';
import {
  collectsEventType,
  collectsInterruptionType,
  isFloorball
} from '../domain/sport';
import {
  TurnoverScenario,
  turnoverScenarios,
  TurnoverType,
  turnoverTypes
} from '../domain/turnover';
import {
  GameRestartOption,
  GameRestartReason,
  GameRestartType,
  gameRestartReasonsFor,
  gameRestartTypesFor,
  optionForHotkey
} from '../domain/game-restart';
import { AlertService } from '../services/alert.service';
import { GameService } from '../services/game.service';
import { MatDialog } from '@angular/material/dialog';
import { ShotImpactDialogComponent } from '../game-events/shot-impact-dialog.component';
import { StrengthStateService } from '../services/strength-state.service';
import {
  selectAssist1PlayerNumber,
  selectAssist2PlayerNumber,
  selectDeflectorPlayerNumber,
  selectEventType,
  selectFaceOffOpponentPlayerNumber,
  selectFouledPlayerNumber,
  selectOfficial1Id,
  selectOfficial2Id,
  selectGameIncidentReason,
  selectGameIncidentSituation,
  selectGameIncidentType,
  selectHas_net_traffic,
  selectHas_screen,
  selectHighlightPlayback,
  selectHighlightRating,
  selectHighlightType,
  selectInterruption_type,
  selectIs_blocked,
  selectNetImpact,
  selectNetTrafficCauserPlayerNumber,
  selectOfficialsCallAction,
  selectOfficialsCallSituation,
  selectPassOutcome,
  selectPassReceiverPlayerNumber,
  selectPassType,
  selectPenaltyId,
  selectPenaltyType,
  selectPeriod,
  selectPlayerNumber,
  selectScreenerPlayerNumber,
  selectShotBlockerPlayerNumber,
  selectShotOutcome,
  selectShotScenario,
  selectShotType,
  selectStrengthState,
  selectTeam,
  selectTeamFaceOffOutcome,
  selectVideoTag,
  selectDefensiveReaction,
  selectGameRestartReason,
  selectTurnoverScenario,
  selectTurnoverOpponentPlayerNumber,
  selectTurnoverType,
  selectGameRestartType,
  select_id,
  selectAttackScenario
} from '../state/reducers/game-event.reducer';
import { Store } from '@ngrx/store';

import {
  assist1PlayerNumberChange,
  assist2PlayerNumberChange,
  deflectorPlayerNumberChange,
  eventTypeChange,
  faceoffOpponentPlayerNumberChange,
  fouledPlayerNumberChange,
  official1NumberChange,
  official2NumberChange,
  gameIncidentReasonChange,
  gameIncidentSituationChange,
  gameIncidentTypeChange,
  hasNetTrafficChange,
  hasScreenChange,
  highlightPlaybackChange,
  highlightRatingChange,
  highlightTypeChange,
  interruptionTypeChange,
  isBlockedChange,
  netImpactChange,
  netTrafficCauserPlayerNumberChange,
  officialsCallActionChange,
  officialsCallSituationChange,
  passOutcomeChange,
  passReceiverPlayerNumberChange,
  passTypeChange,
  penaltyDurationChange,
  penaltyTypeChange,
  periodChange,
  playerNumberChange,
  screenerPlayerNumberChange,
  shotBlockerPlayerNumberChange,
  shotOutcomeChange,
  shotScenarioChange,
  shotTypeChange,
  strengthStateChange,
  teamChange,
  teamFaceoffOutcomeChange,
  videoReviewTypeChange,
  videoTagChange,
  defensiveReactionChange,
  gameRestartReasonChange,
  turnoverOpponentPlayerNumberChange,
  turnoverScenarioChange,
  turnoverTypeChange,
  gameRestartTypeChange,
  attackScenarioChange
} from '../state/actions/game-event.action';
import { GlobalState } from '../state/reducers';
import ObjectId from 'bson-objectid';
import { ShortcutDefinition } from '../domain/shortcut.model';
import { VideoKeyboardShortcutService } from '../video-keyboard-shortcut/video-keyboard-shortcut.service';
import { NGXLogger } from 'ngx-logger';
import { GameIncidentService } from '../services/game-incident.service';

// A key can stand for different interruptions in different sports (k: puck
// stopped by goalkeeper in ice hockey, kicking in floorball); the first type
// the game's sport collects wins.
const INTERRUPTION_TYPE_HOTKEYS: [number, InterruptionType][] = [
  [CharCodes.g_key_code, 'goal'],
  [CharCodes.i_key_code, 'icing'],
  [CharCodes.k_key_code, 'puck_blocked_by_goalkeeper'],
  [CharCodes.o_key_code, 'offside'],
  [CharCodes.p_key_code, 'puck_out'],
  [CharCodes.t_key_code, 'timeout'],
  [CharCodes.u_key_code, 'unspecified'],
  [CharCodes.y_key_code, 'penalty'],
  [CharCodes.r_key_code, 'incorrect_pass_back'],
  [CharCodes.h_key_code, 'high_stick'],
  [CharCodes.k_key_code, 'kicking_violation'],
  [CharCodes.s_key_code, 'stick_hit'],
  [CharCodes.c_key_code, 'body_contact'],
  [CharCodes.b_key_code, 'obstruction'],
  [CharCodes.f_key_code, 'foul'],
  [CharCodes.o_key_code, 'official_touches_ball'],
  [CharCodes.n_key_code, 'unnatural_interruption']
];

@Component({
  selector: 'app-video-keyboard-actions',
  templateUrl: './video-keyboard-actions.component.html',
  styleUrls: ['./video-keyboard-actions.component.css']
})
export class VideoKeyboardActionsComponent implements OnInit, OnDestroy {
  private componentDestroyed$: Subject<void> = new Subject();
  _id: string;
  period = '';
  team: string;
  whichteam: string;
  eventType: EventType;
  interruptionType: InterruptionType;
  videoTag: VideoTagType;
  defensiveReaction: DefensiveReaction;
  readonly defensiveReactions = defensiveReactions;
  gameRestartType: GameRestartType;
  gameRestartReason: GameRestartReason;
  turnoverType: TurnoverType;
  turnoverScenario: TurnoverScenario;
  readonly turnoverTypes = turnoverTypes;
  readonly turnoverScenarios = turnoverScenarios;
  shotOutcome: ShotOutcome;
  shotScenario: ShotScenario;
  shotType: ShotType;
  is_blocked: string;
  has_net_traffic: string;
  has_screen: string;
  teamFaceOffOutcome: TeamFaceOffOutcome;
  passType: PassType;
  passOutcome: PassOutcome;
  playerNumberString = '';
  player = null;
  playerShoots = 'left';
  playerPosition = '';
  passReceiverPlayerNumberString = '';
  passReceiverPlayer = null;
  shotBlockerPlayerNumberString = '';
  shotBlockerPlayer = null;
  assist1PlayerNumberString = '';
  assist1Player = null;
  assist2PlayerNumberString = '';
  assist2Player = null;
  deflectorPlayerNumberString = '';
  deflectorPlayer = null;
  screenerPlayerNumberString = '';
  screenerPlayer = null;
  netTrafficCauserPlayerNumberString = '';
  netTrafficCauserPlayer = null;
  faceOffOpponentPlayerNumberString = '';
  faceOffOpponentPlayer = null;
  fouledPlayerNumberString = '';
  turnoverOpponentPlayerNumberString = '';
  turnoverOpponentPlayer = null;
  fouledPlayer = null;
  netImpactX: number;
  netImpactY: number;
  highlightType: HighlightType = null;
  highlightPlayback: HighlightPlayback = null;
  highlightRating: HighlightRating = null;
  gameIncidentReason: GameIncidentReason = null;
  gameIncidentReasonType: GameIncidentReasonType = null;
  gameIncidentSituation: GameIncidentSituation = null;
  gameIncidentType: GameIncidentType = null;
  officialsCallAction: OfficialsCallAction = null;
  officialsCallSituation: OfficialsCallSituation = null;
  readonly rolesInGame: Partial<Record<GameIncidentType, OfficialRoleInGame>> =
    {
      penalty: 'head',
      offside: 'linesperson',
      icing: 'linesperson'
    };

  official1: GameOfficial = null;
  official1Str: string = '';
  official2: GameOfficial = null;
  official2Str: string = '';
  strengthState: StrengthState;
  penaltyType: PenaltyType;
  penaltyId: ObjectId;
  attackScenario: AttackScenario = null;

  players: any[] = [];

  @Input()
  game: Game;

  get hasOfficiatingFullCollection() {
    return this.game.officiatingDataSet === 'FULL';
  }

  @Output()
  save = new EventEmitter<void>();

  @Output()
  keyTyped = new EventEmitter<void>();

  constructor(
    private broadcast: BroadcastService,
    private alertService: AlertService,
    private gameService: GameService,
    private playerService: PlayerService,
    private strengthStateService: StrengthStateService,
    private videoKeyboardShortcutService: VideoKeyboardShortcutService,
    private dialog: MatDialog,
    private store: Store<GlobalState>,
    private logger: NGXLogger,
    private gameIncidentService: GameIncidentService
  ) {}

  async ngOnInit() {
    this.players = await this.playerService.loadPlayers(this.game);
    this.subscribeToReduxEvents();
    this.broadcast
      .listen()
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((message) => {
        switch (message.data.type) {
          case BroadcastAction.ResetState:
            this.resetHotKeys();
            break;
          case BroadcastAction.SaveComplete:
            this.handleSavingCompleted();
            break;
          case BroadcastAction.SaveFailed:
            this.handleSavingFailed(message.data.error);
            break;
        }
      });
  }

  ngOnDestroy() {
    this.componentDestroyed$.next();
  }

  subscribeToReduxEvents() {
    this.store
      .select(select_id)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((_id) => (this._id = _id));
    this.store
      .select(selectEventType)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => (this.eventType = newValue));
    this.store
      .select(selectTeam)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => (this.team = newValue));
    this.store
      .select(selectInterruption_type)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => (this.interruptionType = newValue));
    this.store
      .select(selectGameIncidentReason)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => (this.gameIncidentReason = newValue));
    this.store
      .select(selectGameIncidentSituation)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => (this.gameIncidentSituation = newValue));
    this.store
      .select(selectGameIncidentType)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => (this.gameIncidentType = newValue));
    this.store
      .select(selectOfficialsCallAction)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => (this.officialsCallAction = newValue));
    this.store
      .select(selectOfficialsCallSituation)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => (this.officialsCallSituation = newValue));
    this.store
      .select(selectOfficial1Id)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((officialState) => {
        if (!officialState || officialState.remove) {
          this.resetOfficial('official1');
        } else {
          const official = this.findOfficialById(officialState.officialId);
          if (official) {
            this.official1 = official;
            this.official1Str = `${official.jerseyNumber}`;
          }
        }
      });
    this.store
      .select(selectOfficial2Id)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((officialState) => {
        if (!officialState || officialState.remove) {
          this.resetOfficial('official2');
        } else {
          const official = this.findOfficialById(officialState.officialId);
          if (official) {
            this.official2 = official;
            this.official2Str = `${official.jerseyNumber}`;
          }
        }
      });
    this.store
      .select(selectVideoTag)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => (this.videoTag = newValue));
    this.store
      .select(selectDefensiveReaction)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => (this.defensiveReaction = newValue));
    this.store
      .select(selectGameRestartType)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => (this.gameRestartType = newValue));
    this.store
      .select(selectGameRestartReason)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => (this.gameRestartReason = newValue));
    this.store
      .select(selectTurnoverType)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => (this.turnoverType = newValue));
    this.store
      .select(selectTurnoverScenario)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => (this.turnoverScenario = newValue));
    this.store
      .select(selectShotOutcome)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => (this.shotOutcome = newValue));
    this.store
      .select(selectAttackScenario)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => (this.attackScenario = newValue));
    this.store
      .select(selectNetImpact)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => (this.netImpactX = newValue?.netImpactX));
    this.store
      .select(selectNetImpact)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => (this.netImpactY = newValue?.netImpactY));
    this.store
      .select(selectShotScenario)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => (this.shotScenario = newValue));
    this.store
      .select(selectShotType)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => (this.shotType = newValue));
    this.store
      .select(selectIs_blocked)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => (this.is_blocked = newValue));
    this.store
      .select(selectHas_net_traffic)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => (this.has_net_traffic = newValue));
    this.store
      .select(selectHas_screen)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => (this.has_screen = newValue));
    this.store
      .select(selectTeamFaceOffOutcome)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => (this.teamFaceOffOutcome = newValue));
    this.store
      .select(selectPassType)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => (this.passType = newValue));
    this.store
      .select(selectPassOutcome)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => (this.passOutcome = newValue));
    this.store
      .select(selectPlayerNumber)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => (this.player = newValue));
    this.store
      .select(selectPassReceiverPlayerNumber)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => (this.passReceiverPlayer = newValue));
    this.store
      .select(selectShotBlockerPlayerNumber)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => (this.shotBlockerPlayer = newValue));
    this.store
      .select(selectDeflectorPlayerNumber)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => (this.deflectorPlayer = newValue));
    this.store
      .select(selectScreenerPlayerNumber)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => (this.screenerPlayer = newValue));
    this.store
      .select(selectNetTrafficCauserPlayerNumber)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => (this.netTrafficCauserPlayer = newValue));
    this.store
      .select(selectFaceOffOpponentPlayerNumber)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => (this.faceOffOpponentPlayer = newValue));
    this.store
      .select(selectFouledPlayerNumber)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => (this.fouledPlayer = newValue));
    this.store
      .select(selectTurnoverOpponentPlayerNumber)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => (this.turnoverOpponentPlayer = newValue));
    this.store
      .select(selectPeriod)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => {
        this.period = newValue;
      });
    this.store
      .select(selectHighlightType)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => (this.highlightType = newValue));
    this.store
      .select(selectHighlightRating)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => (this.highlightRating = newValue));
    this.store
      .select(selectHighlightPlayback)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => (this.highlightPlayback = newValue));
    this.store
      .select(selectStrengthState)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => (this.strengthState = newValue));
    this.store
      .select(selectPenaltyType)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => (this.penaltyType = newValue));
    this.store
      .select(selectPenaltyId)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => (this.penaltyId = newValue));
    this.store
      .select(selectAssist1PlayerNumber)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => (this.assist1Player = newValue));
    this.store
      .select(selectAssist2PlayerNumber)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((newValue) => (this.assist2Player = newValue));
  }

  @HostListener('window:keydown', ['$event'])
  hotkeys(event: KeyboardEvent) {
    if (event.keyCode === CharCodes.escape_key_code) {
      this.handleResetHotKey();
    }

    const eventType = this.eventType;
    if (this.isPassEventTypeHotKey(event.keyCode, event.key, eventType)) {
      this.handlePassEventTypeHotKey(event, eventType);
    } else if (
      this.isShotEventTypeHotKey(event.keyCode, event.key, eventType)
    ) {
      this.handleShotEventTypeHotKey(event, eventType);
    } else if (this.isVideoTagEventType(event.keyCode, event.key, eventType)) {
      this.handleVideoTagEventType(event, eventType);
    } else if (this.isGameRestartEventType(event.keyCode, eventType)) {
      this.handleGameRestartEventType(event, eventType);
    } else if (this.isTurnoverEventType(event.keyCode, event.key, eventType)) {
      this.handleTurnoverEventType(event, eventType);
    } else if (
      this.isFaceOffEventTypeHotKey(event.keyCode, event.key, eventType)
    ) {
      this.handleFaceOffEventTypeHotKey(event, eventType);
    } else if (
      this.isPenaltyEventTypeHotKey(event.keyCode, event.key, eventType)
    ) {
      this.handlePenaltyEventTypeHotKey(event, eventType);
    } else if (
      this.isHighlightEventTypeHotKey(event.keyCode, event.key, eventType)
    ) {
      this.handleHighlightEventTypeHotKey(event, eventType);
    } else if (this.isInterruptionEventTypePeriod(event.keyCode, eventType)) {
      this.handleInterruptionEventTypePeriod(event, eventType);
    } else if (
      this.isInterruptionEventTypeNotPeriod(event.keyCode, eventType)
    ) {
      this.handleInterruptionEventTypeNotPeriod(event, eventType);
    } else if (this.isPuckPossessionEventType(event.keyCode, eventType)) {
      this.handlePuckPossessionEventType(event, eventType);
    } else if (this.isOddMenRushEventType(event.keyCode, eventType)) {
      this.handleOddMenRushEventType(event, eventType);
    } else if (this.isGameIncidentEventType(event, eventType)) {
      this.handleGameIncidentEventType(event, eventType);
    } else if (this.isTeamHotKey(event.keyCode)) {
      this.handleTeamHotKey(event, eventType);
    } else if (this.isNumberHotKey(event.key)) {
      this.handlePlayerNumberHotKey(event);
    } else if (this.isHighlightVideoReviewEventType(event.keyCode, eventType)) {
      this.handleHighlightVideoReviewEventType(event.keyCode);
    } else if (this.isSaveHotKey(event.keyCode)) {
      console.log('handleSaveHotKey', event);
      this.handleSaveHotKey();
    }

    if (eventType) {
      this.keyTyped.emit();
    }
  }

  private isNumberHotKey(key): boolean {
    return !(isNaN(key) || key === null || key === ' ');
  }

  private handlePlayerNumberHotKey(event: KeyboardEvent) {
    if (this.playerNumberString.length <= 1) {
      this.playerNumberString += event.key;
      const player = this.lookupPlayer(this.playerNumberString, false);
      if (player) {
        this.player = player;
        this.store.dispatch(playerNumberChange({ playerNumber: this.player }));

        this.playerShoots = this.getPlayerShoots(player);
        this.playerPosition = this.getPlayerPosition(player);
      }
    } else if (this.playerNumberString.length > 1) {
      this.playerNumberString = '';
      this.player = null;
      this.playerShoots = null;
      this.playerPosition = null;
    }
  }

  private getPlayerShoots(player: string) {
    if (!this.team) {
      return null;
    }
    const playerId = this.game.getPlayerIdByJerseyNumber(
      this.team === this.game.homeTeam,
      player
    );
    const playerObj = this.players.find((p) => p.id === playerId);
    return playerObj ? playerObj.shoots : null;
  }

  private getPlayerPosition(player: string) {
    if (!this.team) {
      return null;
    }
    const playerId = this.game.getPlayerIdByJerseyNumber(
      this.team === this.game.homeTeam,
      player
    );
    const playerObj = this.players.find((p) => p.id === playerId);
    return playerObj?.position?.substring(0, 1);
  }

  private lookupPlayer(playerNumber: string, lookInOpponentTeam: boolean) {
    let player = null;
    const lookupInAwayTeam =
      (this.whichteam === 'homeTeam' && lookInOpponentTeam) ||
      (this.whichteam === 'awayTeam' && !lookInOpponentTeam);
    if (lookupInAwayTeam) {
      player = this.gameService.getAwayTeamPlayerByJerseyNumber(
        this.game,
        playerNumber
      );
    } else {
      player = this.gameService.getHomeTeamPlayerByJerseyNumber(
        this.game,
        playerNumber
      );
    }
    return player;
  }

  private findOfficialById(officialId: number): GameOfficial | undefined {
    return this.game.officials.find(
      (official) => official.officialId === officialId
    );
  }

  private findOfficialByJerseyNumber(
    jerseyNumber: string
  ): GameOfficial | undefined {
    return this.game.officials
      .filter((o) => !this.isInvalidOfficialRoleForGameIncidentType(o))
      .find((o) => o.jerseyNumber?.toString() === jerseyNumber);
  }

  private findOfficial(
    roleInGame: OfficialRoleInGame,
    sequenceNumber: number
  ): GameOfficial | undefined {
    return this.game.officials.find(
      (o) =>
        o.officialRoleInGame === roleInGame &&
        o.sequenceNumber === sequenceNumber
    );
  }

  private isPassEventTypeHotKey(keyCode, key, eventType) {
    return (
      (eventType == null && keyCode === CharCodes.p_key_code) ||
      (eventType === 'pass' &&
        (keyCode === CharCodes.p_key_code ||
          keyCode === CharCodes.c_key_code ||
          keyCode === CharCodes.s_key_code ||
          keyCode === CharCodes.r_key_code)) ||
      (eventType === 'pass' &&
        (this.passType === 'pass' || this.passType === 'shot_pass') &&
        (keyCode === CharCodes.c_key_code ||
          keyCode === CharCodes.i_key_code ||
          this.isNumberHotKey(key))) ||
      (eventType === 'pass' &&
        this.passType === 'chip' &&
        (keyCode === CharCodes.x_key_code ||
          keyCode === CharCodes.b_key_code ||
          keyCode === CharCodes.i_key_code ||
          keyCode === CharCodes.f_key_code ||
          this.isNumberHotKey(key))) ||
      (eventType === 'pass' &&
        this.passType === 'rim' &&
        (keyCode === CharCodes.x_key_code ||
          keyCode === CharCodes.b_key_code ||
          keyCode === CharCodes.f_key_code ||
          this.isNumberHotKey(key)))
    );
  }

  private handlePassEventTypeHotKey(event: KeyboardEvent, eventType) {
    if (eventType !== 'pass') {
      // newly entered pass
      this.setPassEventType();
    } else {
      // handle pass details
      this.handlePassDetails(event);
    }
  }

  private handlePassDetails(event: KeyboardEvent) {
    if (this.passType == null) {
      this.handleNewlyEnteredPassType(event);
    } else {
      if (this.passType === 'pass' || this.passType === 'shot_pass') {
        if (this.isNumberHotKey(event.key)) {
          this.handlePlayerNumberHotKeyForPassReceiver(event);
        } else {
          this.handlePassOutcome(event);
        }
      }
    }
  }

  private handlePlayerNumberHotKeyForPassReceiver(event: KeyboardEvent) {
    if (this.passReceiverPlayerNumberString.length <= 1) {
      this.passReceiverPlayerNumberString += event.key;
      const player = this.lookupPlayer(
        this.passReceiverPlayerNumberString,
        false
      );
      if (player) {
        this.passReceiverPlayer = player;
        this.store.dispatch(
          passReceiverPlayerNumberChange({
            passReceiverPlayerNumber: this.passReceiverPlayer
          })
        );
      }
    } else if (this.passReceiverPlayerNumberString.length > 1) {
      this.passReceiverPlayerNumberString = '';
      this.passReceiverPlayer = null;
    }
  }

  private handleNewlyEnteredPassType(event: KeyboardEvent) {
    if (event.keyCode === CharCodes.c_key_code) {
      this.passType = 'chip';
    } else if (event.keyCode === CharCodes.r_key_code) {
      this.passType = 'rim';
    } else if (event.keyCode === CharCodes.p_key_code) {
      this.passType = 'pass';
    } else if (event.keyCode === CharCodes.s_key_code) {
      this.passType = 'shot_pass';
    }
    this.store.dispatch(passTypeChange({ passType: this.passType }));
  }

  private handlePassOutcome(event: KeyboardEvent) {
    if (event.keyCode === CharCodes.c_key_code) {
      this.passOutcome = 'complete';
    } else if (event.keyCode === CharCodes.i_key_code) {
      this.passOutcome = 'incomplete';
    }
    this.store.dispatch(passOutcomeChange({ passOutcome: this.passOutcome }));
  }

  private setPassEventType() {
    this.eventType = 'pass';
    this.store.dispatch(eventTypeChange({ eventType: 'pass' }));
  }

  private isShotEventTypeHotKey(keyCode, key, eventType) {
    return (
      (eventType == null && keyCode === CharCodes.s_key_code) ||
      (eventType === 'shot' &&
        (this.isNumberHotKey(key) ||
          this.isShotOutcomeHotKey(keyCode, eventType) ||
          this.isShotScenarioHotKey(keyCode, eventType) ||
          this.isShotTypeHotKey(keyCode, eventType) ||
          this.isDeflectionTypeHotKey(keyCode, eventType) ||
          this.hasNetTrafficTypeHotKey(keyCode, eventType) ||
          this.hasScreenTypeHotKey(keyCode, eventType) ||
          this.isAttackScenarioHotkey(keyCode, eventType))) ||
      (eventType === 'shot' &&
        (this.shotOutcome === 'blocked' ||
          this.has_net_traffic === '1' ||
          this.has_screen === '1') &&
        this.isNumberHotKey(key))
    );
  }

  private isShotOutcomeHotKey(keyCode, eventType) {
    return (
      eventType === 'shot' &&
      this.shotOutcome == null &&
      (keyCode === CharCodes.m_key_code ||
        keyCode === CharCodes.b_key_code ||
        keyCode === CharCodes.i_key_code ||
        keyCode === CharCodes.g_key_code ||
        keyCode === CharCodes.o_key_code)
    );
  }

  private isShotScenarioHotKey(keyCode, eventType) {
    return (
      eventType === 'shot' &&
      this.shotOutcome != null &&
      (keyCode === CharCodes.r_key_code ||
        keyCode === CharCodes.o_key_code ||
        keyCode === CharCodes.e_key_code ||
        keyCode === CharCodes.q_key_code)
    );
  }

  private isShotTypeHotKey(keyCode, eventType) {
    return (
      eventType === 'shot' &&
      this.shotOutcome != null &&
      this.shotScenario != null &&
      (keyCode === CharCodes.w_key_code ||
        keyCode === CharCodes.s_key_code ||
        keyCode === CharCodes.d_key_code ||
        keyCode === CharCodes.b_key_code)
    );
  }

  private isDeflectionTypeHotKey(keyCode, eventType) {
    return (
      eventType === 'shot' &&
      this.shotOutcome != null &&
      this.shotScenario != null &&
      this.shotType != null &&
      (keyCode === CharCodes.y_key_code || keyCode === CharCodes.n_key_code)
    );
  }

  private hasNetTrafficTypeHotKey(keyCode, eventType) {
    return (
      eventType === 'shot' &&
      this.shotOutcome != null &&
      this.shotScenario != null &&
      this.shotType != null &&
      this.has_net_traffic == null &&
      (keyCode === CharCodes.y_key_code || keyCode === CharCodes.n_key_code)
    );
  }

  private hasScreenTypeHotKey(keyCode, eventType) {
    return (
      eventType === 'shot' &&
      this.shotOutcome != null &&
      this.shotScenario != null &&
      this.shotType != null &&
      this.has_net_traffic != null &&
      this.has_screen == null &&
      (keyCode === CharCodes.y_key_code || keyCode === CharCodes.n_key_code)
    );
  }

  private handleShotEventTypeHotKey(event: KeyboardEvent, eventType) {
    if (eventType !== 'shot') {
      this.store.dispatch(eventTypeChange({ eventType: 'shot' }));
    } else {
      this.handleShotDetails(event, eventType);
    }
  }

  private handleShotDetails(event: KeyboardEvent, eventType) {
    if (this.isNumberHotKey(event.key)) {
      if (
        this.shotOutcome === 'blocked' &&
        this.shotType !== 'deflection' &&
        (this.has_net_traffic === undefined || this.has_net_traffic === '0')
      ) {
        this.handlePlayerNumberHotKeyForShotBlocker(event);
      } else if (
        this.shotType === 'deflection' &&
        (this.has_net_traffic === undefined || this.has_net_traffic === '0')
      ) {
        this.handlePlayerNumberHotKeyForDeflector(event);
      } else if (
        this.has_net_traffic === '1' &&
        this.has_screen === undefined
      ) {
        this.handlePlayerNumberHotKeyForNetTrafficCauser(event);
      } else if (this.has_screen === '1') {
        this.handlePlayerNumberHotKeyForScreener(event);
      } else {
        if (!this.shotOutcome && this.shotOutcome !== 'goal') {
          this.handlePlayerNumberHotKey(event);
        } else {
          if (!this.player || (this.player && !this.game.isAssistsCollection)) {
            this.handlePlayerNumberHotKey(event);
          } else if (!this.assist1Player) {
            this.handlePlayerNumberHotKeyForAssist(event, 'assist1');
          } else if (!this.assist2Player) {
            this.handlePlayerNumberHotKeyForAssist(event, 'assist2');
          }
        }
      }
    } else if (this.isShotOutcomeHotKey(event.keyCode, eventType)) {
      this.handleShotOutcomeHotKey(event);
    } else if (this.isAttackScenarioHotkey(event.keyCode, eventType)) {
      this.handleAttackScenarioHotKey(event);
    } else if (this.isShotScenarioHotKey(event.keyCode, eventType)) {
      this.handleShotScenarioHotKey(event);
    } else if (this.isShotTypeHotKey(event.keyCode, eventType)) {
      this.handleShotTypeHotKey(event);
    } else if (this.hasNetTrafficTypeHotKey(event.keyCode, eventType)) {
      this.handleShotNetTrafficHotKey(event);
    } else if (this.hasScreenTypeHotKey(event.keyCode, eventType)) {
      this.handleShotScreenHotKey(event);
    }
  }

  private handlePlayerNumberHotKeyForAssist(
    event: KeyboardEvent,
    assistType: 'assist1' | 'assist2'
  ) {
    let playerNumberString: string;
    let playerNumber: any;
    let assistAction: any;

    if (assistType === 'assist1') {
      playerNumberString = this.assist1PlayerNumberString;
      playerNumber = this.assist1Player;
      assistAction = assist1PlayerNumberChange;
    } else {
      playerNumberString = this.assist2PlayerNumberString;
      playerNumber = this.assist2Player;
      assistAction = assist2PlayerNumberChange;
    }

    // Update the player number string
    if (playerNumberString.length <= 1) {
      playerNumberString += event.key;
      const player = this.lookupPlayer(playerNumberString, false);

      if (player) {
        playerNumber = player;
        this.store.dispatch(
          assistAction({ [assistType + 'PlayerNumber']: player })
        );
      }
    } else {
      playerNumberString = '';
      playerNumber = null;
    }

    // Apply updated values back to the instance
    if (assistType === 'assist1') {
      this.assist1PlayerNumberString = playerNumberString;
      this.assist1Player = playerNumber;
    } else {
      this.assist2PlayerNumberString = playerNumberString;
      this.assist2Player = playerNumber;
    }
  }

  private handleShotOutcomeHotKey(event: KeyboardEvent) {
    if (event.keyCode === CharCodes.b_key_code) {
      this.shotOutcome = 'blocked';
      this.store.dispatch(isBlockedChange({ is_blocked: this.is_blocked }));
    } else if (event.keyCode === CharCodes.m_key_code) {
      this.shotOutcome = 'miss';
    } else if (event.keyCode === CharCodes.g_key_code) {
      this.shotOutcome = 'goal';
    } else if (event.keyCode === CharCodes.o_key_code) {
      this.shotOutcome = 'on_goal';
    } else if (event.keyCode === CharCodes.i_key_code) {
      this.shotOutcome = 'iron';
    }
    this.store.dispatch(shotOutcomeChange({ shotOutcome: this.shotOutcome }));

    if (
      !this.game.isLiveCollection &&
      this.game.dataSet === DataSet.FULL &&
      ['goal', 'on_goal', 'iron'].includes(this.shotOutcome)
    ) {
      this.showImpactDialog();
    }
  }

  private handlePlayerNumberHotKeyForShotBlocker(event: KeyboardEvent) {
    if (this.shotBlockerPlayerNumberString.length <= 1) {
      this.shotBlockerPlayerNumberString += event.key;
      const player = this.lookupPlayer(
        this.shotBlockerPlayerNumberString,
        true
      );
      if (player) {
        this.shotBlockerPlayer = player;
        this.store.dispatch(
          shotBlockerPlayerNumberChange({
            shotBlockerPlayerNumber: this.shotBlockerPlayer
          })
        );
      }
    } else if (this.shotBlockerPlayerNumberString.length > 1) {
      this.shotBlockerPlayerNumberString = '';
      this.shotBlockerPlayer = null;
    }
  }

  private handlePlayerNumberHotKeyForScreener(event: KeyboardEvent) {
    if (this.screenerPlayerNumberString.length <= 1) {
      this.screenerPlayerNumberString += event.key;
      const player = this.lookupPlayer(this.screenerPlayerNumberString, false);
      if (player) {
        this.screenerPlayer = player;
        this.store.dispatch(
          screenerPlayerNumberChange({
            screenerPlayerNumber: this.screenerPlayer
          })
        );
      }
    } else if (this.screenerPlayerNumberString.length > 1) {
      this.screenerPlayerNumberString = '';
      this.screenerPlayer = null;
    }
  }

  private handlePlayerNumberHotKeyForNetTrafficCauser(event: KeyboardEvent) {
    if (this.netTrafficCauserPlayerNumberString.length <= 1) {
      this.netTrafficCauserPlayerNumberString += event.key;
      const player = this.lookupPlayer(
        this.netTrafficCauserPlayerNumberString,
        false
      );
      if (player) {
        this.netTrafficCauserPlayer = player;
        this.store.dispatch(
          netTrafficCauserPlayerNumberChange({
            netTrafficCauserPlayerNumber: this.netTrafficCauserPlayer
          })
        );
      }
    } else if (this.netTrafficCauserPlayerNumberString.length > 1) {
      this.netTrafficCauserPlayerNumberString = '';
      this.netTrafficCauserPlayer = null;
    }
  }

  private handlePlayerNumberHotKeyForDeflector(event: KeyboardEvent) {
    if (this.deflectorPlayerNumberString.length <= 1) {
      this.deflectorPlayerNumberString += event.key;
      const player = this.lookupPlayer(this.deflectorPlayerNumberString, false);
      if (player) {
        this.deflectorPlayer = player;
        this.store.dispatch(
          deflectorPlayerNumberChange({
            deflectorPlayerNumber: this.deflectorPlayer
          })
        );
      }
    } else if (this.deflectorPlayerNumberString.length > 1) {
      this.deflectorPlayerNumberString = '';
      this.deflectorPlayer = null;
    }
  }

  private handleShotScenarioHotKey(event: KeyboardEvent) {
    if (event.keyCode === CharCodes.r_key_code) {
      this.shotScenario = 'regular';
    } else if (event.keyCode === CharCodes.q_key_code) {
      this.shotScenario = 'quick_release';
    } else if (event.keyCode === CharCodes.e_key_code) {
      this.shotScenario = 'rebound';
    } else if (event.keyCode === CharCodes.o_key_code) {
      this.shotScenario = 'one_timer';
    }
    this.store.dispatch(
      shotScenarioChange({ shotScenario: this.shotScenario })
    );
  }

  private handleShotTypeHotKey(event: KeyboardEvent) {
    if (event.keyCode === CharCodes.w_key_code) {
      this.shotType = 'wrist_shot';
    } else if (event.keyCode === CharCodes.s_key_code) {
      this.shotType = 'slap_shot';
    } else if (event.keyCode === CharCodes.b_key_code) {
      this.shotType = 'backhand_shot';
    } else if (event.keyCode === CharCodes.d_key_code) {
      this.shotType = 'deflection';
    }
    this.store.dispatch(shotTypeChange({ shotType: this.shotType }));
  }

  private handleShotNetTrafficHotKey(event: KeyboardEvent) {
    if (event.keyCode === CharCodes.y_key_code) {
      this.has_net_traffic = '1';
    } else if (event.keyCode === CharCodes.n_key_code) {
      this.has_net_traffic = '0';
    }
    this.store.dispatch(
      hasNetTrafficChange({ has_net_traffic: this.has_net_traffic })
    );
  }

  private handleShotScreenHotKey(event: KeyboardEvent) {
    if (event.keyCode === CharCodes.y_key_code) {
      this.has_screen = '1';
    } else if (event.keyCode === CharCodes.n_key_code) {
      this.has_screen = '0';
    }
    this.store.dispatch(hasScreenChange({ has_screen: this.has_screen }));
  }

  private isFaceOffEventTypeHotKey(keyCode, key, eventType) {
    return (
      (eventType == null &&
        keyCode === CharCodes.f_key_code &&
        this.collectsEventType('face_off')) ||
      (eventType === 'face_off' && this.isFaceOffDetailHotKey(keyCode, key))
    );
  }

  private isFaceOffDetailHotKey(keyCode, key) {
    return (
      this.isNumberHotKey(key) ||
      keyCode === CharCodes.w_key_code ||
      keyCode === CharCodes.l_key_code ||
      keyCode === CharCodes.n_key_code
    );
  }

  private handleFaceOffEventTypeHotKey(event: KeyboardEvent, eventType) {
    if (eventType !== 'face_off') {
      this.store.dispatch(eventTypeChange({ eventType: 'face_off' }));
    } else {
      // handle face_off details
      this.handleFaceOffDetails(event);
    }
  }

  private handleFaceOffDetails(event: KeyboardEvent) {
    if (this.isNumberHotKey(event.key)) {
      if (this.teamFaceOffOutcome == null) {
        this.handlePlayerNumberHotKey(event);
      } else {
        this.handlePlayerNumberHotKeyForFaceOffOpponent(event);
      }
    } else if (this.isFaceOffDetailHotKey(event.keyCode, event.key)) {
      if (this.teamFaceOffOutcome == null) {
        if (event.keyCode === CharCodes.w_key_code) {
          this.teamFaceOffOutcome = 'win';
        } else if (event.keyCode === CharCodes.l_key_code) {
          this.teamFaceOffOutcome = 'lost';
        }
        this.store.dispatch(
          teamFaceoffOutcomeChange({
            teamFaceOffOutcome: this.teamFaceOffOutcome
          })
        );
      }
    }
  }

  private handlePlayerNumberHotKeyForFaceOffOpponent(event: KeyboardEvent) {
    if (this.faceOffOpponentPlayerNumberString.length <= 1) {
      this.faceOffOpponentPlayerNumberString += event.key;
      const player = this.lookupPlayer(
        this.faceOffOpponentPlayerNumberString,
        true
      );
      if (player) {
        this.faceOffOpponentPlayer = player;
        this.store.dispatch(
          faceoffOpponentPlayerNumberChange({
            faceOffOpponentPlayerNumber: this.faceOffOpponentPlayer
          })
        );
      }
    } else if (this.faceOffOpponentPlayerNumberString.length > 1) {
      this.faceOffOpponentPlayerNumberString = '';
      this.faceOffOpponentPlayer = null;
    }
  }

  private isPenaltyEventTypeHotKey(
    keyCode: number,
    key: string,
    eventType: EventType
  ) {
    return (
      (keyCode === CharCodes.y_key_code &&
        eventType !== 'interruption' &&
        eventType !== 'game_incident') ||
      (eventType === 'penalty' &&
        this.player !== null &&
        this.isNumberHotKey(key))
    );
  }

  private handlePenaltyEventTypeHotKey(event: KeyboardEvent, eventType) {
    if (eventType !== 'penalty') {
      if (this.game.isRealtimeCollection) {
        this.handlePenaltyEventStartInLiveDraftGame();
      }
      this.store.dispatch(eventTypeChange({ eventType: 'penalty' }));
    } else if (this.isNumberHotKey(event.key)) {
      this.handlePlayerNumberHotKeyForFouledPlayer(event);
    }
  }

  private handlePlayerNumberHotKeyForFouledPlayer(event: KeyboardEvent) {
    if (this.fouledPlayerNumberString.length <= 1) {
      this.fouledPlayerNumberString += event.key;
      const player = this.lookupPlayer(this.fouledPlayerNumberString, true);
      if (player) {
        this.fouledPlayer = player;
        this.store.dispatch(
          fouledPlayerNumberChange({ fouledPlayerNumber: this.fouledPlayer })
        );
      }
    } else if (this.fouledPlayerNumberString.length > 1) {
      this.fouledPlayerNumberString = '';
      this.fouledPlayer = null;
    }
  }

  private handlePlayerNumberHotKeyForTurnoverOpponent(event: KeyboardEvent) {
    if (this.turnoverOpponentPlayerNumberString.length <= 1) {
      this.turnoverOpponentPlayerNumberString += event.key;
      const player = this.lookupPlayer(
        this.turnoverOpponentPlayerNumberString,
        true
      );
      if (player) {
        this.turnoverOpponentPlayer = player;
        this.store.dispatch(
          turnoverOpponentPlayerNumberChange({
            turnoverOpponentPlayerNumber: this.turnoverOpponentPlayer
          })
        );
      }
    } else if (this.turnoverOpponentPlayerNumberString.length > 1) {
      this.turnoverOpponentPlayerNumberString = '';
      this.turnoverOpponentPlayer = null;
    }
  }

  private isInterruptionEventTypeNotPeriod(
    keyCode: number,
    eventType: EventType
  ) {
    return (
      (!eventType && keyCode === CharCodes.i_key_code) ||
      (eventType === 'interruption' &&
        this.interruptionTypeForKey(keyCode) != null)
    );
  }

  private interruptionTypeForKey(keyCode: number): InterruptionType {
    return INTERRUPTION_TYPE_HOTKEYS.find(
      ([key, interruptionType]) =>
        key === keyCode && this.collectsInterruptionType(interruptionType)
    )?.[1];
  }

  private isGameIncidentEventType(event: KeyboardEvent, eventType: EventType) {
    if (!this.game?.hasOfficiatingCollection()) {
      return false;
    }

    return (
      (!eventType && event.keyCode === CharCodes.g_key_code) ||
      (eventType === 'game_incident' &&
        (this.isNumberHotKey(event.key) ||
          this.videoKeyboardShortcutService.hasShortcutForKey(
            [
              ...this.getGameIncidentTypeShortcuts(),
              ...this.getOfficialsCallSituationsShortcuts(),
              ...this.getOfficialsCallActionShortcuts(),
              ...this.getPhysicalFoulsShortcuts(),
              ...this.getRestrainingFoulsShortcuts(),
              ...this.getStickFoulsShortcuts(),
              ...this.getOtherFoulsShortcuts(),
              ...this.getOfficialsShortcuts()
            ],
            event.key
          )))
    );
  }

  private isPuckPossessionEventType(keyCode: number, eventType: EventType) {
    if (
      eventType === 'game_incident' ||
      !this.collectsEventType('puckPossession')
    ) {
      return false;
    }
    return keyCode === CharCodes.u_key_code;
  }

  private isOddMenRushEventType(keyCode, eventType) {
    return (
      eventType == null &&
      keyCode === CharCodes.o_key_code &&
      this.collectsEventType('oddMenRush')
    );
  }

  private isVideoTagEventType(keyCode, key, eventType) {
    return (
      (!eventType && keyCode === CharCodes.v_key_code) ||
      (eventType === 'videoTag' && this.isVideoTagSelectionHotKey(keyCode)) ||
      (eventType === 'videoTag' && this.isDefensiveReactionHotKey(keyCode)) ||
      (eventType === 'videoTag' &&
        this.videoTag != null &&
        this.isNumberHotKey(key))
    );
  }

  private isVideoTagSelectionHotKey(keyCode: number): boolean {
    return this.videoTagForKey(keyCode) != null;
  }

  private videoTagForKey(keyCode: number): VideoTagType {
    switch (keyCode) {
      case CharCodes.d_key_code:
        return 'low_break_out_under_pressure';
      case CharCodes.f_key_code:
        return 'board_sector_break_out_under_pressure';
      case CharCodes.b_key_code:
        return 'd_offensive_blue_line_puck_management';
      case CharCodes.z_key_code:
        return 'controlled_offensive_zone_entry';
      case CharCodes.s_key_code:
        return 'set_break_out';
      default:
        return undefined;
    }
  }

  // The reaction keys only mean something once the set break out tag is chosen, so a
  // dropped 's' keystroke turns them into a no-op instead of another video tag.
  private isDefensiveReactionHotKey(keyCode: number): boolean {
    return (
      this.videoTag === 'set_break_out' &&
      this.defensiveReactionForKey(keyCode) != null
    );
  }

  private defensiveReactionForKey(keyCode: number): DefensiveReaction {
    return defensiveReactions.find(
      (reaction) => reaction.hotkey.toUpperCase().charCodeAt(0) === keyCode
    )?.value;
  }

  // Restart type first, then its reason; both answer to letters that mean
  // something else elsewhere, so each is only live at its own step.
  private isGameRestartEventType(keyCode: number, eventType: EventType) {
    return (
      (eventType == null &&
        keyCode === CharCodes.r_key_code &&
        this.collectsEventType('gameRestart')) ||
      (eventType === 'gameRestart' &&
        this.gameRestartDetailForKey(keyCode) != null)
    );
  }

  private gameRestartDetailForKey(
    keyCode: number
  ): GameRestartType | GameRestartReason {
    if (this.gameRestartType == null) {
      return optionForHotkey(this.gameRestartTypes, keyCode);
    }
    if (this.gameRestartReason == null) {
      return optionForHotkey(this.gameRestartReasons, keyCode);
    }
    return undefined;
  }

  private handleGameRestartEventType(event: KeyboardEvent, eventType) {
    if (eventType !== 'gameRestart') {
      this.store.dispatch(eventTypeChange({ eventType: 'gameRestart' }));
    } else if (this.gameRestartType == null) {
      this.gameRestartType = optionForHotkey(
        this.gameRestartTypes,
        event.keyCode
      );
      this.store.dispatch(
        gameRestartTypeChange({ gameRestartType: this.gameRestartType })
      );
    } else {
      this.gameRestartReason = optionForHotkey(
        this.gameRestartReasons,
        event.keyCode
      );
      this.store.dispatch(
        gameRestartReasonChange({ gameRestartReason: this.gameRestartReason })
      );
    }
  }

  // Turnover type first, then the possession change; like the restart, each
  // step only answers to its own letters. Jersey digits typed before the
  // possession change are the player who lost the ball, digits typed after it
  // the opponent who won it (like the face-off opponent after w/l).
  private isTurnoverEventType(
    keyCode: number,
    key: string,
    eventType: EventType
  ) {
    return (
      (eventType == null &&
        keyCode === CharCodes.l_key_code &&
        this.collectsEventType('turnover')) ||
      (eventType === 'turnover' &&
        this.turnoverDetailForKey(keyCode) != null) ||
      (eventType === 'turnover' &&
        this.turnoverScenario != null &&
        this.isNumberHotKey(key))
    );
  }

  private turnoverDetailForKey(
    keyCode: number
  ): TurnoverType | TurnoverScenario {
    if (this.turnoverType == null) {
      return optionForHotkey(turnoverTypes, keyCode);
    }
    if (this.turnoverScenario == null) {
      return optionForHotkey(turnoverScenarios, keyCode);
    }
    return undefined;
  }

  private handleTurnoverEventType(event: KeyboardEvent, eventType) {
    if (eventType !== 'turnover') {
      this.store.dispatch(eventTypeChange({ eventType: 'turnover' }));
    } else if (this.isNumberHotKey(event.key)) {
      this.handlePlayerNumberHotKeyForTurnoverOpponent(event);
    } else if (this.turnoverType == null) {
      this.turnoverType = optionForHotkey(turnoverTypes, event.keyCode);
      this.store.dispatch(
        turnoverTypeChange({ turnoverType: this.turnoverType })
      );
    } else {
      this.turnoverScenario = optionForHotkey(turnoverScenarios, event.keyCode);
      this.store.dispatch(
        turnoverScenarioChange({ turnoverScenario: this.turnoverScenario })
      );
    }
  }

  private handleInterruptionEventTypeNotPeriod(event, eventType: EventType) {
    if (eventType !== 'interruption') {
      this.store.dispatch(eventTypeChange({ eventType: 'interruption' }));
    } else if (this.interruptionType == null) {
      this.interruptionType = this.interruptionTypeForKey(event.keyCode);

      this.store.dispatch(
        interruptionTypeChange({ interruption_type: this.interruptionType })
      );
    }
  }

  private handleVideoTagEventType(event: KeyboardEvent, eventType) {
    if (eventType !== 'videoTag') {
      this.store.dispatch(eventTypeChange({ eventType: 'videoTag' }));
    } else if (this.videoTag == null) {
      this.videoTag = this.videoTagForKey(event.keyCode);
      if (this.videoTag === 'd_offensive_blue_line_puck_management') {
        this.checkIfPlayerMatchesDefensemenVideoTag();
      }
      this.store.dispatch(videoTagChange({ videoTag: this.videoTag }));
    } else if (this.isDefensiveReactionHotKey(event.keyCode)) {
      this.defensiveReaction = this.defensiveReactionForKey(event.keyCode);
      this.store.dispatch(
        defensiveReactionChange({ defensiveReaction: this.defensiveReaction })
      );
    }
  }

  private checkIfPlayerMatchesDefensemenVideoTag() {
    if (this.whichteam === 'homeTeam') {
      if (
        !this.gameService.getHomeTeamDefensemen(this.game).includes(this.player)
      ) {
        this.alertService.showWarning(
          `Player '${this.player}' is not part of the home team defensemen!`
        );
      }
    } else if (this.whichteam === 'awayTeam') {
      if (
        !this.gameService.getAwayTeamDefensemen(this.game).includes(this.player)
      ) {
        this.alertService.showWarning(
          `Player '${this.player}' is not part of the away team defensemen!`
        );
      }
    }
  }

  private handlePuckPossessionEventType(event: KeyboardEvent, eventType) {
    if (eventType !== 'puckPossession') {
      this.setPuckPossessionEventType();
    }
  }

  private setPuckPossessionEventType() {
    this.eventType = 'puckPossession';
    this.store.dispatch(eventTypeChange({ eventType: 'puckPossession' }));
  }

  private handleOddMenRushEventType(event, eventType) {
    if (eventType !== 'oddMenRush') {
      this.setOddMenRushEventType();
    }
  }

  private setOddMenRushEventType() {
    this.eventType = 'oddMenRush';
    this.store.dispatch(eventTypeChange({ eventType: 'oddMenRush' }));
  }

  private handleGameIncidentEventType(
    event: KeyboardEvent,
    eventType: EventType
  ) {
    if (eventType !== 'game_incident') {
      this.store.dispatch(eventTypeChange({ eventType: 'game_incident' }));
    } else if (!this.gameIncidentType) {
      const type = this.videoKeyboardShortcutService.findValueForShortcutKey(
        this.getGameIncidentTypeShortcuts(),
        event.key
      );
      if (type) {
        this.gameIncidentType = type;
        this.store.dispatch(
          gameIncidentTypeChange({
            gameIncidentType: this.gameIncidentType
          })
        );
      }
    } else if (
      this.isCallableGameIncidentEvent() &&
      !this.officialsCallAction
    ) {
      const callAction =
        this.videoKeyboardShortcutService.findValueForShortcutKey(
          this.getOfficialsCallActionShortcuts(),
          event.key
        );

      if (callAction) {
        this.officialsCallAction = callAction;
        this.store.dispatch(
          officialsCallActionChange({
            officialsCallAction: this.officialsCallAction
          })
        );
        if (this.hasOfficiatingFullCollection) {
          if (callAction === 'call' && this.gameIncidentType === 'icing') {
            // set first linesperson for icing calls per default
            const linesperson1 = this.findOfficial('linesperson', 1);
            if (linesperson1) {
              this.official1 = linesperson1;
              this.store.dispatch(
                official1NumberChange({ officialId: linesperson1.officialId })
              );
            }
          } else if (
            callAction === 'non-call' &&
            this.gameIncidentType !== 'offside'
          ) {
            const roleInGame = this.rolesInGame[this.gameIncidentType];
            if (roleInGame) {
              // set first official for non-call situations per default, except for offside
              const otherOfficial = this.findOfficial(roleInGame, 1);
              if (otherOfficial) {
                this.official1 = otherOfficial;
                this.store.dispatch(
                  official1NumberChange({
                    officialId: otherOfficial.officialId
                  })
                );
              }
            }
          }
        }
      }
    } else if (
      this.hasOfficiatingFullCollection &&
      !!this.officialsCallAction &&
      (!this.official1 ||
        (this.isNumberHotKey(event.key) && !this.officialsCallSituation))
    ) {
      this.handleOfficial1NumberHotKey(event);
    } else if (
      !this.officialsCallSituation &&
      (this.official1 || !this.hasOfficiatingFullCollection)
    ) {
      const callSituation =
        this.videoKeyboardShortcutService.findValueForShortcutKey(
          this.getOfficialsCallSituationsShortcuts(),
          event.key
        );

      if (callSituation) {
        this.store.dispatch(
          officialsCallSituationChange({
            officialsCallSituation: callSituation
          })
        );
        if (this.hasOfficiatingFullCollection) {
          if (this.officialsCallAction === 'call') {
            if (this.gameIncidentType === 'icing') {
              // set second linesperson for icing calls per default
              const otherLinesperson = this.findOtherOfficial(
                'linesperson',
                this.official1
              );
              if (otherLinesperson) {
                this.official2 = otherLinesperson;
                this.store.dispatch(
                  official2NumberChange({
                    officialId: otherLinesperson.officialId
                  })
                );
              }
            }
          } else if (
            this.officialsCallAction === 'non-call' &&
            this.gameIncidentType !== 'offside'
          ) {
            // set second official for non-call situations per default, except for offside
            const roleInGame = this.rolesInGame[this.gameIncidentType];
            if (roleInGame) {
              const otherOfficial = this.findOtherOfficial(
                roleInGame,
                this.official1
              );
              if (otherOfficial) {
                this.official2 = otherOfficial;
                this.store.dispatch(
                  official2NumberChange({
                    officialId: otherOfficial.officialId
                  })
                );
              }
            }
          }
        }
      }
    } else if (
      this.official1 &&
      this.officialsCallAction &&
      this.gameIncidentType !== 'penalty' &&
      this.hasOfficiatingFullCollection
    ) {
      if (this.isNumberHotKey(event.key)) {
        this.handleOfficial2NumberHotKey(event);
      }
    } else if (
      (!this.gameIncidentReason || !this.official2) &&
      this.officialsCallAction &&
      this.officialsCallSituation &&
      this.gameIncidentType === 'penalty'
    ) {
      if (this.isNumberHotKey(event.key) && this.hasOfficiatingFullCollection) {
        this.handleOfficial2NumberHotKey(event);
      } else if (this.gameIncidentReasonType) {
        const reason =
          this.videoKeyboardShortcutService.findValueForShortcutKey(
            this.getGameIncidentReasonShortcutsForType(
              this.gameIncidentReasonType
            ),
            event.key
          );
        if (reason) {
          this.gameIncidentReason = reason;
        }

        this.store.dispatch(
          gameIncidentReasonChange({
            gameIncidentReason: this.gameIncidentReason
          })
        );
      } else {
        const reasonType =
          this.videoKeyboardShortcutService.findValueForShortcutKey(
            this.getGameIncidentReasonTypeShortcuts(),
            event.key
          );

        if (reasonType) {
          this.gameIncidentReasonType = reasonType;
        }
      }
    } else if (
      this.gameIncidentType === 'situation' &&
      !this.gameIncidentSituation
    ) {
      const situation =
        this.videoKeyboardShortcutService.findValueForShortcutKey(
          this.getGameIncidentSituationShortcuts(),
          event.key
        );
      if (situation) {
        this.gameIncidentSituation = situation;
        this.store.dispatch(
          gameIncidentSituationChange({
            gameIncidentSituation: this.gameIncidentSituation
          })
        );
      }
    }
  }

  private findOtherOfficial(
    roleInGame: OfficialRoleInGame,
    official: GameOfficial
  ) {
    // assuming there are only 1 and 2 are the only linespersons
    const firstLinesperson = this.findOfficial(roleInGame, 1);
    const secondLinesperson = this.findOfficial(roleInGame, 2);
    if (official.officialId === firstLinesperson.officialId) {
      return secondLinesperson;
    } else if (official.officialId === secondLinesperson.officialId) {
      return firstLinesperson;
    } else {
      return undefined;
    }
  }

  private isTeamHotKey(keyCode) {
    return (
      keyCode === CharCodes.h_key_code ||
      keyCode === CharCodes.a_key_code ||
      keyCode === CharCodes.n_key_code
    );
  }

  private handleOfficialNumberHotKey(
    event: KeyboardEvent,
    officialType: 'official1' | 'official2'
  ) {
    if (!this.isNumberHotKey(event.key)) {
      return;
    }
    const MAX_OFFICIAL_NUMBER_LENGTH = 2;
    const isOfficial1 = officialType === 'official1';
    const officialStr = isOfficial1 ? this.official1Str : this.official2Str;
    const dispatchAction = isOfficial1
      ? official1NumberChange
      : official2NumberChange;

    if (officialStr.length >= MAX_OFFICIAL_NUMBER_LENGTH) {
      const { resetOfficial, resetAction } = this.resetOfficial(officialType);
      if (resetOfficial) {
        this.store.dispatch(
          resetAction({
            officialId: resetOfficial.officialId,
            remove: true
          })
        );
      }
      return;
    }

    if (isOfficial1) {
      this.official1Str += event.key;
    } else {
      this.official2Str += event.key;
    }

    const newOfficial = this.findOfficialByJerseyNumber(
      isOfficial1 ? this.official1Str : this.official2Str
    );
    const currentOfficial = isOfficial1 ? this.official1 : this.official2;

    if (currentOfficial) {
      this.store.dispatch(
        dispatchAction({
          officialId: currentOfficial.officialId,
          remove: true
        })
      );
    }

    if (
      !isOfficial1 &&
      newOfficial?.officialId === this.official1?.officialId
    ) {
      return;
    }

    if (isOfficial1) {
      this.official1 = newOfficial;
    } else {
      this.official2 = newOfficial;
    }

    if (newOfficial) {
      this.store.dispatch(
        dispatchAction({
          officialId: newOfficial.officialId
        })
      );
    }
  }

  private resetOfficial(officialType: 'official1' | 'official2') {
    const isOfficial1 = officialType === 'official1';
    const resetOfficial = isOfficial1 ? this.official1 : this.official2;
    const resetAction = isOfficial1
      ? official1NumberChange
      : official2NumberChange;

    if (isOfficial1) {
      this.official1 = null;
      this.official1Str = '';
    } else {
      this.official2 = null;
      this.official2Str = '';
    }
    return { resetOfficial, resetAction };
  }

  private handleOfficial1NumberHotKey(event: KeyboardEvent): void {
    this.handleOfficialNumberHotKey(event, 'official1');
  }

  private handleOfficial2NumberHotKey(event: KeyboardEvent): void {
    this.handleOfficialNumberHotKey(event, 'official2');
  }

  private handleTeamHotKey(event: KeyboardEvent, eventType: EventType) {
    if (event.keyCode === CharCodes.h_key_code) {
      this.team = this.game.homeTeam;
      this.whichteam = 'homeTeam';
    } else if (event.keyCode === CharCodes.a_key_code) {
      this.team = this.game.awayTeam;
      this.whichteam = 'awayTeam';
    } else if (event.keyCode === CharCodes.n_key_code) {
      this.team = Team.NEUTRAL_TEAM;
      this.whichteam = Team.NEUTRAL_TEAM;
    }

    if (this.game.isRealtimeCollection && eventType === 'penalty') {
      this.handlePenaltyStrengthState();
    }
    this.store.dispatch(teamChange({ team: this.team }));
  }

  private isSaveHotKey(keyCode) {
    return keyCode === CharCodes.enter_key_code;
  }

  private handleSaveHotKey() {
    this.broadcast.postMessage({ type: BroadcastAction.Save });
  }

  private isHighlightEventTypeHotKey(keyCode, key, eventType) {
    return (
      this.game.isHighlightCollection &&
      ((!eventType && keyCode === CharCodes.h_key_code) ||
        (eventType === 'highlight' &&
          this.team &&
          (keyCode === CharCodes.h_key_code ||
            keyCode === CharCodes.a_key_code) &&
          !this.highlightType) ||
        (eventType === 'highlight' &&
          !this.highlightType &&
          (keyCode === CharCodes.g_key_code ||
            keyCode === CharCodes.s_key_code ||
            keyCode === CharCodes.b_key_code ||
            keyCode === CharCodes.i_key_code ||
            keyCode === CharCodes.c_key_code ||
            keyCode === CharCodes.t_key_code ||
            keyCode === CharCodes.e_key_code ||
            keyCode === CharCodes.n_key_code ||
            keyCode === CharCodes.p_key_code ||
            keyCode === CharCodes.o_key_code ||
            keyCode === CharCodes.l_key_code ||
            keyCode === CharCodes.v_key_code)) ||
        (this.highlightType &&
          !(
            this.highlightType === 'goal' ||
            this.highlightType === 'save' ||
            this.highlightType === 'penalty_shot' ||
            this.highlightType === 'video_review'
          ) &&
          !this.highlightPlayback &&
          (keyCode === CharCodes.n_key_code ||
            keyCode === CharCodes.s_key_code)) ||
        (this.highlightType &&
          ((this.highlightPlayback &&
            !(
              this.highlightType === 'titles' ||
              this.highlightType === 'video_review'
            )) ||
            this.highlightType === 'goal' ||
            this.highlightType === 'save' ||
            this.highlightType === 'penalty_shot') &&
          this.isNumberHotKey(key)))
    );
  }

  private handleHighlightEventTypeHotKey(event: KeyboardEvent, eventType) {
    if (eventType !== 'highlight') {
      // newly entered highlight
      this.setHighlightEventType();
    } else {
      // handle highlight details
      this.handleHighlightDetails(event);
    }
  }

  private setHighlightEventType() {
    this.eventType = 'highlight';
    this.store.dispatch(eventTypeChange({ eventType: 'highlight' }));
  }

  private handleHighlightDetails(event: KeyboardEvent) {
    if (!this.highlightType) {
      this.handleNewlyEnteredHighlightType(event);
    } else if (
      !this.highlightPlayback &&
      !(
        this.highlightType === 'goal' ||
        this.highlightType === 'save' ||
        this.highlightType === 'penalty_shot' ||
        this.highlightType === 'video_review'
      )
    ) {
      this.handleNewlyEnteredHighlightPlayback(event);
    } else {
      if (this.isNumberHotKey(event.key)) {
        this.handleRatingNumberHotKeyForHighlight(event);
      }
    }
  }

  private handleNewlyEnteredHighlightType(event: KeyboardEvent) {
    if (event.keyCode === CharCodes.g_key_code) {
      this.highlightType = 'goal';
    } else if (event.keyCode === CharCodes.s_key_code) {
      this.highlightType = 'shot';
    } else if (event.keyCode === CharCodes.a_key_code) {
      this.highlightType = 'save';
    } else if (event.keyCode === CharCodes.h_key_code) {
      this.highlightType = 'hit';
    } else if (event.keyCode === CharCodes.b_key_code) {
      this.highlightType = 'blooper';
    } else if (event.keyCode === CharCodes.i_key_code) {
      this.highlightType = 'highlight_play';
    } else if (event.keyCode === CharCodes.c_key_code) {
      this.highlightType = 'close_up';
    } else if (event.keyCode === CharCodes.t_key_code) {
      this.highlightType = 'titles';
    } else if (event.keyCode === CharCodes.e_key_code) {
      this.highlightType = 'bench';
    } else if (event.keyCode === CharCodes.n_key_code) {
      this.highlightType = 'stands';
    } else if (event.keyCode === CharCodes.p_key_code) {
      this.highlightType = 'penalty_box';
    } else if (event.keyCode === CharCodes.o_key_code) {
      this.highlightType = 'compilation';
    } else if (event.keyCode === CharCodes.l_key_code) {
      this.highlightType = 'penalty_shot';
    } else if (event.keyCode === CharCodes.v_key_code) {
      this.highlightType = 'video_review';
    }

    this.store.dispatch(
      highlightTypeChange({ highlightType: this.highlightType })
    );
  }

  private handleNewlyEnteredHighlightPlayback(event: KeyboardEvent) {
    if (event.keyCode === CharCodes.n_key_code) {
      this.highlightPlayback = 'normal';
    } else if (event.keyCode === CharCodes.s_key_code) {
      this.highlightPlayback = 'slow_motion';
    }

    this.store.dispatch(
      highlightPlaybackChange({ highlightPlayback: this.highlightPlayback })
    );
  }

  private handleRatingNumberHotKeyForHighlight(event: KeyboardEvent) {
    const rating = parseInt(event.key, 10) as HighlightRating;
    if (rating > 0 && rating < 6) {
      this.highlightRating = rating;
      this.store.dispatch(
        highlightRatingChange({ highlightRating: this.highlightRating })
      );
    }
  }

  ratingChange(value) {
    this.highlightRating = value === 0 ? null : value;
    this.store.dispatch(
      highlightRatingChange({ highlightRating: this.highlightRating })
    );
  }

  handleResetHotKey() {
    this.resetHotKeys();
    this.broadcast.postMessage({ type: BroadcastAction.ResetState });
  }

  private isInterruptionEventTypePeriod(keyCode, eventType) {
    return (
      (eventType === null && keyCode === CharCodes.t_key_code) ||
      (eventType === null && keyCode === CharCodes.e_key_code) ||
      (eventType === 'interruption' &&
        this.interruptionType === 'period_start' &&
        !this.isSaveHotKey(keyCode))
    );
  }

  private handleInterruptionEventTypePeriod(event: KeyboardEvent, eventType) {
    if (eventType !== 'interruption') {
      if (event.keyCode === CharCodes.t_key_code) {
        this.store.dispatch(
          interruptionTypeChange({ interruption_type: 'period_start' })
        );
      } else if (event.keyCode === CharCodes.e_key_code) {
        this.store.dispatch(
          interruptionTypeChange({ interruption_type: 'period_end' })
        );
      }
      setTimeout(
        () =>
          this.store.dispatch(eventTypeChange({ eventType: 'interruption' })),
        0
      );
    } else if (
      eventType === 'interruption' &&
      this.interruptionType === 'period_start'
    ) {
      const period = this.gameService.findPeriodByKeyCode(event.keyCode);
      if (period) {
        this.onPeriodChange(period as GamePeriod);
      }
    }
  }

  private resetHotKeys() {
    this.team = null;
    this.playerNumberString = '';
    this.passReceiverPlayerNumberString = '';
    this.shotBlockerPlayerNumberString = '';
    this.deflectorPlayerNumberString = '';
    this.screenerPlayerNumberString = '';
    this.netTrafficCauserPlayerNumberString = '';
    this.faceOffOpponentPlayerNumberString = '';
    this.fouledPlayerNumberString = '';
    this.turnoverOpponentPlayerNumberString = '';
    this.assist1PlayerNumberString = '';
    this.assist2PlayerNumberString = '';
    this.gameIncidentReasonType = null;
    this.official1 = null;
    this.official2 = null;
    this.official1Str = '';
    this.official2Str = '';
  }

  private handleSavingFailed(errorMessage: string) {
    this.alertService.showError('Saving event failed: ' + errorMessage);
  }

  private handleSavingCompleted() {
    this.alertService.showInfo('Saving event completed!');
  }

  get playerWarning() {
    return (
      this.eventType === 'face_off' && this.isNotForward(this.team, this.player)
    );
  }

  isInvalidOfficialRoleForGameIncidentType(official: GameOfficial): boolean {
    if (!this.isCallableGameIncidentEvent()) {
      return true;
    }
    if (this.gameIncidentType === 'penalty') {
      return official.officialRoleInGame !== 'head';
    } else if (
      this.gameIncidentType === 'offside' ||
      this.gameIncidentType === 'icing'
    ) {
      return official.officialRoleInGame !== 'linesperson';
    }
  }

  get faceOffOpponentPlayerWarning() {
    if (!this.game) {
      return false;
    }
    const opponentTeam =
      this.team === this.game.homeTeam
        ? this.game.awayTeam
        : this.game.homeTeam;
    return (
      this.eventType === 'face_off' &&
      this.isNotForward(opponentTeam, this.faceOffOpponentPlayer)
    );
  }

  get gameRestartTypes(): GameRestartOption<GameRestartType>[] {
    return gameRestartTypesFor(this.game?.sport);
  }

  get gameRestartReasons(): GameRestartOption<GameRestartReason>[] {
    return gameRestartReasonsFor(this.game?.sport);
  }

  get isFloorball(): boolean {
    return isFloorball(this.game?.sport);
  }

  collectsEventType(eventType: EventType): boolean {
    return collectsEventType(this.game?.sport, eventType);
  }

  collectsInterruptionType(interruptionType: InterruptionType): boolean {
    return collectsInterruptionType(this.game?.sport, interruptionType);
  }

  get editMode() {
    return !!this._id;
  }

  private isNotForward(team: string, player: string) {
    if (!team || !player || !this.game) {
      return false;
    }
    return !this.game.getForwards(team).includes(player);
  }

  showImpactDialog() {
    const dialogRef = this.dialog.open(ShotImpactDialogComponent, {
      width: '320px',
      height: '350px',
      data: {
        netImpactX: null,
        netImpactY: null
      }
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.netImpactX && result.netImpactY) {
        this.store.dispatch(
          netImpactChange({
            netImpact: {
              netImpactX: result.netImpactX,
              netImpactY: result.netImpactY
            }
          })
        );
      }
    });
  }

  requiresTeam() {
    const teamNotRequiredEventTypes: EventType[] = [
      'interruption',
      'game_incident'
    ];
    return (
      this.eventType && !teamNotRequiredEventTypes.includes(this.eventType)
    );
  }

  requiresPlayer() {
    const playerNotRequiredEventTypes: EventType[] = [
      'interruption',
      'game_incident'
    ];
    const playerNotRequiredEventTypesLiveDraftEvent: EventType[] = [
      'shot',
      'penalty'
    ];
    return (
      this.eventType &&
      !playerNotRequiredEventTypes.includes(this.eventType) &&
      !(
        playerNotRequiredEventTypesLiveDraftEvent.includes(this.eventType) &&
        this.game.isRealtimeCollection
      )
    );
  }

  onPeriodChange(period: GamePeriod) {
    this.period = period;
    this.store.dispatch(periodChange({ period }));
  }

  handlePenaltyStrengthState() {
    if (!this.penaltyType) {
      return;
    }
    const direction = this.penaltyType === 'start' ? -1 : 1;
    const result = this.strengthStateService.changeStrengthState(
      this.strengthState,
      this.game.homeTeam === this.team,
      direction
    );
    // Only emit if valid strength state
    if (result) {
      this.strengthState = result;
      this.store.dispatch(strengthStateChange({ strengthState: result }));
    }
  }

  handlePenaltyEventStartInLiveDraftGame() {
    this.penaltyType = 'start';
    this.store.dispatch(penaltyTypeChange({ penaltyType: 'start' }));
    this.store.dispatch(penaltyDurationChange({ penaltyDuration: '2' }));

    // If team is already selected calculate strength
    if (this.game.isRealtimeCollection && this.team) {
      this.handlePenaltyStrengthState();
    }
  }

  getGameIncidentTypeShortcuts(): ShortcutDefinition<GameIncidentType>[] {
    return [
      { shortcutKey: 'p', value: 'penalty', label: 'Penalty' },
      { shortcutKey: 'i', value: 'icing', label: 'Icing' },
      { shortcutKey: 'o', value: 'offside', label: 'Offside' },
      { shortcutKey: 's', value: 'situation', label: 'Situation' }
    ];
  }

  getOfficialsCallActionShortcuts(): ShortcutDefinition<OfficialsCallAction>[] {
    return [
      { shortcutKey: 'c', value: 'call', label: 'Call' },
      { shortcutKey: 'n', value: 'non-call', label: 'Non-Call' }
    ];
  }

  getOfficialsCallSituationsShortcuts(): ShortcutDefinition<OfficialsCallSituation>[] {
    return [
      { shortcutKey: 'r', value: 'clear', label: 'Clear' },
      { shortcutKey: 's', value: 'close', label: 'Close' }
    ];
  }

  getGameIncidentReasonShortcutsForType(t: GameIncidentReasonType) {
    switch (t) {
      case 'physical':
        return this.getPhysicalFoulsShortcuts();
      case 'restraining':
        return this.getRestrainingFoulsShortcuts();
      case 'stick':
        return this.getStickFoulsShortcuts();
      case 'other':
        return this.getOtherFoulsShortcuts();
      case 'officials':
        return this.getOfficialsShortcuts();
      default:
        return [];
    }
  }

  getGameIncidentSituationShortcuts(): ShortcutDefinition<GameIncidentSituation>[] {
    return [
      { shortcutKey: 'c', value: 'playerControl', label: 'Player Control' },
      { shortcutKey: 'h', value: 'handpass', label: 'Handpass' },
      {
        shortcutKey: 'o',
        value: 'officialTalkingToPlayerOrCoaches',
        label: 'Official Talking To Player Or Coaches'
      },
      {
        shortcutKey: 'p',
        value: 'highStickingThePuck',
        label: 'High Sticking The Puck'
      },
      { shortcutKey: 'r', value: 'refereeHuddle', label: 'Referee Huddle' },
      { shortcutKey: 's', value: 'scrum', label: 'Scrum' },
      {
        shortcutKey: 't',
        value: 'refereeTangledUpInPlay',
        label: 'Referee Tangled Up In Play'
      }
    ];
  }

  getGameIncidentReasonTypeShortcuts(): ShortcutDefinition<GameIncidentReasonType>[] {
    return [
      { shortcutKey: 'p', value: 'physical', label: 'Physical Fouls' },
      { shortcutKey: 'r', value: 'restraining', label: 'Restraining Fouls' },
      { shortcutKey: 's', value: 'stick', label: 'Stick Fouls' },
      { shortcutKey: 'a', value: 'officials', label: 'Abuse of Officials' },
      { shortcutKey: 'o', value: 'other', label: 'Other Fouls' }
    ];
  }

  getPhysicalFoulsShortcuts(): ShortcutDefinition<GameIncidentReason>[] {
    return [
      { shortcutKey: 'b', value: 'boarding', label: 'Boarding' },
      { shortcutKey: 'o', value: 'bodycheck', label: 'Bodycheck' },
      {
        shortcutKey: 'c',
        value: 'checkFromBehind',
        label: 'Check from Behind'
      },
      {
        shortcutKey: 'h',
        value: 'checkToTheHeadOrNeck',
        label: 'Check to the Head/Neck'
      },
      {
        shortcutKey: 'g',
        value: 'grabbingFaceMask',
        label: 'Grabbing Face Mask'
      },
      { shortcutKey: 'a', value: 'charging', label: 'Charging' },
      { shortcutKey: 'l', value: 'clipping', label: 'Clipping' },
      { shortcutKey: 'e', value: 'elbowing', label: 'Elbowing' },
      { shortcutKey: 'f', value: 'fighting', label: 'Fighting' },
      { shortcutKey: 't', value: 'headButting', label: 'Head-butting' },
      { shortcutKey: 'k', value: 'kicking', label: 'Kicking' },
      { shortcutKey: 'n', value: 'kneeing', label: 'Kneeing' },
      { shortcutKey: 'r', value: 'roughing', label: 'Roughing' },
      { shortcutKey: 's', value: 'slewFooting', label: 'Slew-footing' }
    ];
  }

  getRestrainingFoulsShortcuts(): ShortcutDefinition<GameIncidentReason>[] {
    return [
      { shortcutKey: 'h', value: 'holding', label: 'Holding' },
      { shortcutKey: 'o', value: 'hooking', label: 'Hooking' },
      { shortcutKey: 'i', value: 'interference', label: 'Interference' },
      { shortcutKey: 't', value: 'tripping', label: 'Tripping' }
    ];
  }

  getStickFoulsShortcuts(): ShortcutDefinition<GameIncidentReason>[] {
    return [
      { shortcutKey: 'b', value: 'buttEnding', label: 'Butt-ending' },
      { shortcutKey: 'c', value: 'crossChecking', label: 'Cross Checking' },
      { shortcutKey: 'h', value: 'highSticking', label: 'High Sticking' },
      { shortcutKey: 's', value: 'spearing', label: 'Spearing' },
      { shortcutKey: 'l', value: 'slashing', label: 'Slashing' }
    ];
  }

  getOtherFoulsShortcuts(): ShortcutDefinition<GameIncidentReason>[] {
    return [
      {
        shortcutKey: 'u',
        value: 'unsportsmanlike',
        label: 'Unsports-manlike'
      },
      {
        shortcutKey: 'd',
        value: 'delayingTheGame',
        label: 'Delaying the Game'
      },
      {
        shortcutKey: 'p',
        value: 'penaltyShot',
        label: 'Penalty Shot'
      },
      {
        shortcutKey: 'v',
        value: 'divingOrEmbellishment',
        label: 'Diving or Embellishm.'
      },
      { shortcutKey: 'o', value: 'other', label: 'Other' }
    ];
  }

  getOfficialsShortcuts(): ShortcutDefinition<GameIncidentReason>[] {
    return [
      {
        shortcutKey: 'a',
        value: 'abuseOfOfficials',
        label: 'Abuse of Officials'
      }
    ];
  }

  isCallableGameIncidentEvent() {
    return (
      this.eventType === 'game_incident' &&
      this.gameIncidentService.isCallableGameIncidentType(this.gameIncidentType)
    );
  }

  private isHighlightVideoReviewEventType(
    keyCode: number,
    eventType: EventType
  ) {
    if (eventType !== 'highlight') {
      return false;
    }
    if (![CharCodes.c_key_code, CharCodes.v_key_code].includes(keyCode)) {
      return false;
    }
    return this.highlightType === 'video_review';
  }

  private handleHighlightVideoReviewEventType(keycode: number) {
    if (keycode === CharCodes.c_key_code) {
      this.store.dispatch(
        videoReviewTypeChange({ videoReviewType: 'coachesChallenge' })
      );
    } else if (keycode === CharCodes.v_key_code) {
      this.store.dispatch(
        videoReviewTypeChange({ videoReviewType: 'videoReview' })
      );
    }
  }

  private isAttackScenarioHotkey(keyCode, eventType) {
    return (
      eventType === 'shot' &&
      this.shotOutcome != null &&
      this.attackScenario == null &&
      (keyCode === CharCodes.o_key_code ||
        keyCode === CharCodes.z_key_code ||
        keyCode === CharCodes.f_key_code ||
        keyCode === CharCodes.r_key_code)
    );
  }

  private handleAttackScenarioHotKey(event: KeyboardEvent) {
    if (event.keyCode === CharCodes.o_key_code) {
      this.attackScenario = 'OZFO';
    } else if (event.keyCode === CharCodes.z_key_code) {
      this.attackScenario = 'OZ';
    } else if (event.keyCode === CharCodes.f_key_code) {
      this.attackScenario = 'FC';
    } else if (event.keyCode === CharCodes.r_key_code) {
      this.attackScenario = 'Rush';
    }
    this.store.dispatch(
      attackScenarioChange({ attackScenario: this.attackScenario })
    );
  }
}
